import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import LiveTicketScanner from '../utils/liveTicketScanner';
import useDoorQueue, { isOffline } from './useDoorQueue';
import { doorService } from '../services';
import { describeResult, doorKind } from '../components/door/doorKinds';

/**
 * useDoorScanner — the door's scanning engine, shared by the full door page
 * (`DoorScanPage`) and the Host Event DOOR tab, which starts the camera in place.
 *
 * The QR encodes a ticket URL (/ticket/{code}); we pull the UUID and POST it to
 * the door scan endpoint, which resolves it (application OR individual ticket
 * code), enforces host/co-host, and is idempotent (re-scans report "already in").
 *
 * Every result — camera, photo, typed code or check-in by name — goes through
 * one path: it flashes over the camera frame (which keeps scanning underneath)
 * and prepends a row to the session log. At a real door you're looking at the
 * phone held up to you, not at the screen, so the flash is the whole frame.
 */
export const UUID_RE = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/**
 * iOS gets a different scanning engine.
 *
 * html5-qrcode works fine on Android and is left alone there. On iOS it never
 * decoded a ticket: its frame crop is computed from CSS pixels against video
 * pixels, it negotiates whatever resolution the browser offers, and its
 * lifecycle leaves the preview black until the camera is rebuilt. Each of those
 * was worked around in turn and it still would not read a code, so iOS runs on
 * our own full-resolution LiveTicketScanner — it owns the <video> we give it.
 *
 * iPadOS 13+ reports itself as a Mac, so touch points are the tell.
 */
export function isIosDevice(nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav) return false;
  const ua = nav.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Mac/.test(ua) && (nav.maxTouchPoints || 0) > 1;
}

const isFrontLabel = (label) => /front|user|facetime/i.test(label || '');
const isRearLabel = (cam) => /back|rear|environment/i.test(cam?.label || '');

const clock = () => new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toUpperCase();

const blocked = (short, detail) => ({ short, detail });
const permissionRefused = () => blocked(
  'CAMERA BLOCKED · ALLOW ACCESS AND RETRY',
  'Camera permission was refused. Allow it for this site in your browser settings, or use PHOTO or a code.',
);

export default function useDoorScanner({
  eventId,
  kind = 'event',
  regionId = 'door-scan-region',
  onResult,
  /**
   * Where a code goes. Default: the event door (`doorService.scan / checkIn` on `eventId`).
   * A gathering door passes its own `{ scan(code), checkIn(admissionId) }` so a gathering
   * seat is only ever checked against the gathering — never an event id in disguise.
   */
  api = null,
} = {}) {
  const k = doorKind(kind);
  const apiRef = useRef(api);
  useEffect(() => { apiRef.current = api; }, [api]);

  // Offline queue. A door loses signal more than anything else on the platform, and a scanner
  // that stops working is worse than one that records and reconciles.
  const sendToServer = useCallback(
    (code) => (apiRef.current ? apiRef.current.scan(code) : doorService.scan(eventId, code)),
    [eventId],
  );
  const onQueueResolved = useCallback((code, data) => {
    // The server has finally ruled. Say what it decided — including, loudly, when a seat that was
    // waved through offline turns out to be refunded or already used.
    reportRef.current?.({ ...data, synced: true });
  }, []);
  const queue = useDoorQueue({ doorId: eventId, send: sendToServer, onResolved: onQueueResolved });

  // The window between tapping START SCANNING and the stream actually running.
  // The button flips straight away; the video fades in on the first real frame.
  const [starting, setStarting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [camError, setCamError] = useState(null); // { short, detail }
  // Populated after the camera opens, so FLIP only appears when there is
  // somewhere to flip to.
  const [cameras, setCameras] = useState([]);
  const [facingUser, setFacingUser] = useState(false);
  const [flash, setFlash] = useState(null); // { id, tone, title, name, sub }
  const [log, setLog] = useState([]);
  const [readingPhoto, setReadingPhoto] = useState(false);
  // ?debug=1 shows what the scanner is actually seeing. Off by default — this
  // is a door tool, not a lab bench — but it turns "it doesn't scan" into a
  // number without needing the phone in hand.
  const debugCam = typeof window !== 'undefined' && /[?&]debug=1/.test(window.location.search);
  const [camStats, setCamStats] = useState(null);

  const reportRef = useRef(null);
  const flashTimer = useRef(null);
  const qrRef = useRef(null);
  // Which camera is live, so FLIP knows where in the list to go next.
  const activeCamRef = useRef(null);
  // Last measured frame luminance, surfaced in the debug line.
  const frameStatRef = useRef(null);
  // Auto-recoveries used this session, so a genuinely dark doorway can't put us
  // in a restart loop.
  const recoveriesRef = useRef(0);
  const blankRunRef = useRef(0);
  // A recovery takes longer than the watchdog's interval. Without this the next
  // tick starts a second camera on top of the first — two stacked previews.
  const restartingRef = useRef(false);
  // Bumped by every start and stop. A start that resolves after a newer one (or
  // a stop) must not come back to life — START, STOP, START in quick taps.
  const genRef = useRef(0);
  // Starts run one after another, so a second start never builds its preview
  // while the first is still tearing its own down.
  const startChainRef = useRef(Promise.resolve());
  const mountedRef = useRef(true);

  const photoInputRef = useRef(null);
  const liveVideoRef = useRef(null);
  const liveScannerRef = useRef(null);
  const nativeRes = useMemo(() => isIosDevice(), []);
  const busyRef = useRef(false);
  // Debounce identical decodes — html5-qrcode fires many times per second.
  const lastCodeRef = useRef({ code: null, at: 0 });
  // html5-qrcode captures the decode callback once when the camera starts, so
  // anything it reads has to come through a ref to stay current.
  const onResultRef = useRef(onResult);
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);
  const kindRef = useRef(k);
  useEffect(() => { kindRef.current = k; }, [k]);

  useEffect(() => () => {
    mountedRef.current = false;
    if (flashTimer.current) clearTimeout(flashTimer.current);
  }, []);

  /** One path for every outcome: flash over the frame + a session-log row. */
  const report = useCallback((r) => {
    const d = describeResult(kindRef.current, r);
    // A fresh id per result restarts the rise even when the same result repeats.
    setFlash({ ...d, id: Date.now() });
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), d.tone === 'ok' ? 2200 : 3200);
    setLog((prev) => [{
      id: `${Date.now()}-${Math.random()}`,
      tone: d.tone,
      name: d.name || (d.tone === 'no' ? d.title : 'Guest'),
      meta: d.sub,
      time: clock(),
    }, ...prev].slice(0, 40));
    try { if (navigator.vibrate) navigator.vibrate(r.result === 'ok' ? 60 : 30); } catch { /* noop */ }
    onResultRef.current?.(r);
  }, []);
  useEffect(() => { reportRef.current = report; }, [report]);

  const submitCode = useCallback(async (code) => {
    if (busyRef.current) return;
    busyRef.current = true;
    // Already offline? Don't spend three seconds on a request that can't land.
    const r = isOffline()
      ? { success: false, status: null }
      : apiRef.current ? await apiRef.current.scan(code) : await doorService.scan(eventId, code);
    busyRef.current = false;
    if (r.success && r.data) { report(r.data); return; }
    // A refusal from the SERVER carries a status; a network failure doesn't. Only the second
    // kind is queued — a seat the server rejected must never be replayed as if it might pass.
    if (!r.status) {
      queue.enqueue(code);
      report({ result: 'queued', message: 'Saved — will sync when you are back online.' });
      return;
    }
    report({ result: 'invalid', message: r.error || 'Scan failed.' });
  }, [eventId, report, queue]);

  const onDecode = useCallback((text) => {
    // A vendor-offer QR (/offer/{uuid}) is the wrong kind for this door — say so, don't call it invalid.
    if (kindRef.current.noun !== 'offer' && /\/offer\//i.test(text || '')) {
      report({ result: 'wrong_kind' });
      return;
    }
    const m = (text || '').match(UUID_RE);
    if (!m) {
      report({ result: 'invalid', message: 'Not a Portal ticket.' });
      return;
    }
    const code = m[1];
    const now = Date.now();
    if (lastCodeRef.current.code === code && now - lastCodeRef.current.at < 3000) return; // debounce
    lastCodeRef.current = { code, at: now };
    submitCode(code);
  }, [submitCode, report]);

  /** Typed code or pasted URL. Returns true when it went to the server. */
  const submitManual = useCallback(async (value) => {
    const m = String(value || '').trim().match(UUID_RE);
    if (!m) {
      report({ result: 'invalid', message: 'Enter a valid ticket code.' });
      return false;
    }
    await submitCode(m[1]);
    return true;
  }, [submitCode, report]);

  /** Check a party in by name — POST /events/{id}/door/checkin. Same flash, same log. */
  const checkIn = useCallback(async (applicationId) => {
    const r = apiRef.current ? await apiRef.current.checkIn(applicationId) : await doorService.checkIn(eventId, applicationId);
    const data = r.success && r.data ? r.data : { result: 'invalid', message: r.error || 'Could not check in.' };
    report(data);
    return data;
  }, [eventId, report]);

  // ── Camera ────────────────────────────────────────────────────────────────
  // Everything here exists because picking "the back camera" is genuinely hard
  // on iOS. See the ordered attempts in startCamera.

  /**
   * DO NOT ADD `qrbox` BACK. It is why this never scanned on an iPhone.
   *
   * html5-qrcode crops each frame in foreverScan (html5-qrcode.ts ~1180) with
   * two ratios worked out independently:
   *
   *     widthRatio  = video.videoWidth  / video.clientWidth
   *     heightRatio = video.videoHeight / video.clientHeight
   *
   * and applies them to `qrRegion` — the qrbox rectangle, which is in CSS pixel
   * space. That is only correct while the stream's aspect ratio matches the
   * element's rendered aspect ratio, which a square frame over a 16:9 stream
   * never does. With no qrbox, qrRegion defaults to the whole viewfinder, the
   * crop becomes the entire frame, and both ratios cancel out.
   */
  const scanConfig = () => ({ fps: 10 });

  const videoEl = useCallback(() => document.querySelector(`#${regionId} video`), [regionId]);

  /**
   * Does the video actually PAINT? Not "does it claim to be ready".
   *
   * On iOS the black-preview failure reports videoWidth, videoHeight and
   * readyState 4 exactly like a healthy stream. The only honest test is to pull
   * a frame and look at it. Returns { mean, max } luminance over a 32x32
   * sample, or null if the draw itself threw.
   */
  const sampleFrame = (v) => {
    try {
      const c = document.createElement('canvas');
      c.width = 32;
      c.height = 32;
      const ctx = c.getContext('2d');
      ctx.drawImage(v, 0, 0, 32, 32);
      const d = ctx.getImageData(0, 0, 32, 32).data;
      let sum = 0;
      let max = 0;
      for (let i = 0; i < d.length; i += 4) {
        const l = (d[i] + d[i + 1] + d[i + 2]) / 3;
        sum += l;
        if (l > max) max = l;
      }
      return { mean: sum / (d.length / 4), max };
    } catch {
      return null;
    }
  };

  /**
   * iOS hands back a stream that attaches but never paints on the first start.
   * Wait for pixels that are actually there, so the caller can redo the
   * flip-away-and-back dance for the operator. `max` rather than `mean`: a QR
   * against a dark background is mostly dark, but it still has white in it.
   */
  const waitForFrames = async (timeoutMs = 1600) => {
    const deadline = Date.now() + timeoutMs;
    let sawPixels = false;
    while (Date.now() < deadline) {
      const v = videoEl();
      if (v) {
        // eslint-disable-next-line no-await-in-loop
        if (v.paused) { try { await v.play(); } catch { /* autoplay refused */ } }
        if (v.videoWidth > 0 && v.readyState >= 2) {
          const shot = sampleFrame(v);
          // A null sample means drawImage itself failed; treat that as "cannot
          // verify" and accept rather than loop forever.
          if (!shot) return true;
          frameStatRef.current = shot;
          if (shot.max > 8) { sawPixels = true; break; }
        }
      }
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 120));
    }
    return sawPixels;
  };

  const teardown = async () => {
    const qr = qrRef.current;
    if (qr) {
      try { await qr.stop(); } catch { /* noop */ }
      try { await qr.clear(); } catch { /* noop */ }
    }
    qrRef.current = null;
    // clear() does not reliably empty the host when the instance was already in
    // a bad state, and anything it leaves behind becomes a second preview the
    // next start stacks underneath. Own the element.
    const host = document.getElementById(regionId);
    if (host) { while (host.firstChild) host.removeChild(host.firstChild); }
  };

  const listCameras = async () => {
    try { return (await Html5Qrcode.getCameras()) || []; } catch { return []; }
  };

  /**
   * One attempt, on its own instance. html5-qrcode leaves its state machine
   * mid-transition when start() rejects, so a second call on the same object
   * throws "already under transition" and buries the error that matters.
   */
  const attemptStart = useCallback(async (source) => {
    const qr = new Html5Qrcode(regionId);
    // iOS negotiates 480x640 by default — too few pixels per module for a dense
    // ticket QR. Ask for 1080p. `videoConstraints` REPLACES the camera argument
    // inside the library (html5-qrcode.ts ~414), so the selection is repeated
    // here or asking for resolution would silently undo the rear-camera choice.
    const selection = typeof source === 'string' ? { deviceId: { exact: source } } : source;
    const config = {
      ...scanConfig(),
      videoConstraints: {
        ...selection,
        width: { ideal: 1920 },
        height: { ideal: 1080 },
      },
    };
    try {
      await qr.start(source, config, onDecode, () => {}); // per-frame decode errors: ignore
      qrRef.current = qr;
      activeCamRef.current = typeof source === 'string' ? source : null;
      return null;
    } catch (err) {
      try { await qr.clear(); } catch { /* the element is ours to reuse anyway */ }
      return err;
    }
  }, [onDecode, regionId]);

  /** Start on `source` and only call it open once frames actually arrive. */
  const openWith = useCallback(async (source) => {
    for (let attempt = 0; attempt < 3; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      const err = await attemptStart(source);
      if (err) return err;
      // eslint-disable-next-line no-await-in-loop
      if (await waitForFrames()) return null;
      // eslint-disable-next-line no-await-in-loop
      await teardown();
    }
    return Object.assign(
      new Error('the camera is running but iOS is not delivering frames to the page — tap FLIP twice as a workaround'),
      { name: 'BlankStreamError' },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptStart]);

  /** Debug-only: freeze the current frame at full sensor resolution and decode that. */
  const grabFrame = useCallback(async () => {
    const v = videoEl();
    if (!v || !v.videoWidth) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height);
      const { default: QrScanner } = await import('qr-scanner');
      const result = await QrScanner.scanImage(canvas, { returnDetailedScanResult: true });
      const text = typeof result === 'string' ? result : result?.data;
      if (text) onDecode(text);
    } catch {
      setCamError(blocked('NO CODE IN THAT FRAME', 'No code found in that frame. Fill more of the box and hold steady.'));
    }
  }, [onDecode, videoEl]);

  /**
   * PHOTO. `capture` hands the job to the native camera: real autofocus, full
   * sensor resolution, the phone's own image pipeline, and a still to decode.
   * A tap slower than live scanning, and it reads codes video mode can't.
   */
  const handlePhoto = useCallback(async (ev) => {
    const file = ev.target.files && ev.target.files[0];
    // Reset first, or picking the same shot twice never fires onChange again.
    ev.target.value = '';
    if (!file) return;

    setReadingPhoto(true);
    setCamError(null);
    try {
      const { default: QrScanner } = await import('qr-scanner');
      // No scanRegion, so it decodes the photo at full resolution rather than
      // the library's downscaled default.
      const result = await QrScanner.scanImage(file, { returnDetailedScanResult: true });
      const text = typeof result === 'string' ? result : result?.data;
      if (!text) throw new Error('no code');
      onDecode(text);
    } catch {
      setCamError(blocked('NO CODE IN THAT PHOTO', 'No code found in that photo. Fill the frame with the QR square and take another.'));
    } finally {
      if (mountedRef.current) setReadingPhoto(false);
    }
  }, [onDecode]);

  const pickPhoto = useCallback(() => { photoInputRef.current?.click(); }, []);

  const startNativeRes = useCallback(async (gen) => {
    const cancelled = () => gen !== genRef.current;
    setCamError(null);
    setStarting(true);
    // Let the box paint before handing it a stream — iOS will not play into an
    // element with no area.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const scanner = liveScannerRef.current
        || new LiveTicketScanner(liveVideoRef.current, onDecode);
      scanner.video = liveVideoRef.current;
      liveScannerRef.current = scanner;
      if (cancelled()) return;
      activeCamRef.current = await scanner.start();
      if (cancelled()) { scanner.stop(); return; }
      const cams = await scanner.listCameras();
      const list = cams.map((c) => ({ id: c.deviceId, label: c.label }));
      setCameras(list);
      setFacingUser(isFrontLabel(list.find((c) => c.id === activeCamRef.current)?.label));
      setStarting(false);
      setScanning(true);
    } catch (err) {
      if (cancelled()) return;
      setStarting(false);
      setScanning(false);
      setCamError(
        err && err.name === 'NotAllowedError'
          ? permissionRefused()
          : blocked("CAMERA DIDN'T START", `Could not start the camera${err && err.message ? ` — ${err.message}` : ''}. Use PHOTO or a code.`),
      );
    }
  }, [onDecode]);

  const startHtml5 = useCallback(async (gen) => {
    const cancelled = () => gen !== genRef.current;
    setCamError(null);

    // Safari drops navigator.mediaDevices entirely off a secure origin, and the
    // library's own error for that is opaque. Say the useful thing first.
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCamError(blocked(
        'NO CAMERA IN THIS BROWSER',
        window.isSecureContext === false
          ? 'The camera needs a secure (https) connection. Open this page over https, or use PHOTO or a code.'
          : 'This browser will not give the page a camera. Use PHOTO or a code.',
      ));
      return;
    }

    recoveriesRef.current = 0;
    blankRunRef.current = 0;
    setStarting(true);

    // Let the frame paint before starting; iOS will not play into a box with no
    // area, and a bare setTimeout(0) is a macrotask, not a rendered frame.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    // Ordered by how reliably each one lands on the REAR camera, the only one
    // that can focus close enough to read a ticket:
    //   1. exact — the only facingMode constraint WebKit actually honours.
    //   2. the device whose label says back/rear.
    //   3. ideal, for anything that rejected `exact` outright.
    //   4. the last enumerated device, the rear camera on most phones.
    //   5. front, last — better a working scanner than none.
    const sources = [
      async () => ({ facingMode: { exact: 'environment' } }),
      async () => {
        const cams = await listCameras();
        return (cams.find(isRearLabel) || {}).id || null;
      },
      async () => ({ facingMode: { ideal: 'environment' } }),
      async () => {
        const cams = await listCameras();
        return cams.length ? cams[cams.length - 1].id : null;
      },
      async () => ({ facingMode: 'user' }),
    ];

    const errors = [];
    for (const resolve of sources) {
      if (cancelled()) return;
      // eslint-disable-next-line no-await-in-loop
      const source = await resolve().catch(() => null);
      if (!source) continue;
      // eslint-disable-next-line no-await-in-loop
      const err = await openWith(source);
      if (!err) {
        if (cancelled()) { await teardown(); return; }
        setStarting(false);
        setScanning(true);
        listCameras().then((cams) => {
          setCameras(cams);
          const active = cams.find((c) => c.id === activeCamRef.current);
          setFacingUser(source && source.facingMode === 'user' ? true : isFrontLabel(active?.label));
        });
        return;
      }
      errors.push(err);
      // A camera that attaches but never paints means selection already
      // succeeded — every remaining rung would do the same thing.
      if (err.name === 'BlankStreamError') break;
    }

    if (cancelled()) return;
    setStarting(false);
    setScanning(false);
    qrRef.current = null;

    // Report the error worth acting on: a refused prompt or a busy camera is
    // definitive wherever it turned up; otherwise the first failure describes
    // the device best.
    const named = (n) => errors.find((e) => e && (e.name === n));
    const chosen = named('NotAllowedError') || named('NotReadableError') || errors[0];
    const name = chosen && chosen.name;
    const detail = chosen ? (chosen.message || String(chosen)) : '';
    setCamError(
      name === 'NotAllowedError'
        ? permissionRefused()
        : name === 'NotReadableError'
          ? blocked('CAMERA IN USE', 'Another app is holding the camera. Close it and try again, or use PHOTO or a code.')
          : blocked("CAMERA DIDN'T START", `Could not start the camera${detail ? ` — ${detail}` : ''}. Use PHOTO or a code.`),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openWith]);

  const start = useCallback(() => {
    const gen = ++genRef.current;
    const engine = nativeRes ? startNativeRes : startHtml5;
    // The button flips to STOP straight away, even while a previous start unwinds.
    setStarting(true);
    const run = startChainRef.current.then(() => (gen === genRef.current ? engine(gen) : undefined));
    startChainRef.current = run.catch(() => {});
    return run;
  }, [nativeRes, startNativeRes, startHtml5]);

  const stop = useCallback(async () => {
    genRef.current += 1;
    restartingRef.current = false;
    if (liveScannerRef.current) liveScannerRef.current.stop();
    await teardown();
    activeCamRef.current = null;
    if (!mountedRef.current) return;
    setStarting(false);
    setScanning(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Manual override. Auto-selection gets the rear camera on the hardware we
   * know about; this is the escape hatch for the hardware we don't.
   */
  const flip = useCallback(async () => {
    if (nativeRes) {
      const scanner = liveScannerRef.current;
      if (!scanner || cameras.length < 2) return;
      const idx = cameras.findIndex((c) => c.id === activeCamRef.current);
      const next = cameras[(idx + 1) % cameras.length];
      try {
        activeCamRef.current = await scanner.start({ deviceId: { exact: next.id } });
        setFacingUser(isFrontLabel(next.label));
      } catch (err) {
        setCamError(blocked("COULDN'T SWITCH CAMERA", `Could not switch camera — ${err.message || String(err)}.`));
      }
      return;
    }

    const cams = await listCameras();
    if (cams.length < 2) return;
    const idx = cams.findIndex((c) => c.id === activeCamRef.current);
    const next = cams[(idx + 1) % cams.length];
    await stop();
    const gen = ++genRef.current;
    setStarting(true);
    await new Promise((r) => setTimeout(r, 0));
    const err = await openWith(next.id);
    if (gen !== genRef.current) { await teardown(); return; }
    setStarting(false);
    if (err) {
      setCamError(blocked("COULDN'T SWITCH CAMERA", `Could not switch camera — ${err.message || String(err)}.`));
      return;
    }
    setScanning(true);
    setCameras(cams);
    setFacingUser(isFrontLabel(next.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openWith, stop, nativeRes, cameras]);

  /**
   * ?debug=1 only. `frames` is measured off the video itself; `decoded` is
   * sampled from the very canvas html5-qrcode reads, so a bright preview next to
   * a black canvas says the frames never reach the decoder.
   */
  useEffect(() => {
    if (!debugCam || !scanning || nativeRes) { setCamStats(null); return undefined; }
    const tick = () => {
      const v = videoEl();
      const shot = v ? sampleFrame(v) : null;
      if (shot) frameStatRef.current = shot;
      const canvas = document.querySelector(`#${regionId} canvas`);
      let decoded = null;
      if (canvas) {
        try {
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          const w = Math.min(48, canvas.width || 0);
          const h = Math.min(48, canvas.height || 0);
          if (w && h) {
            const d = ctx.getImageData(0, 0, w, h).data;
            let max = 0;
            for (let i = 0; i < d.length; i += 4) {
              const l = (d[i] + d[i + 1] + d[i + 2]) / 3;
              if (l > max) max = l;
            }
            decoded = Math.round(max);
          }
        } catch (e) { decoded = `err:${e.name}`; }
      }
      setCamStats({
        video: v ? `${v.videoWidth}x${v.videoHeight}` : 'none',
        client: v ? `${v.clientWidth}x${v.clientHeight}` : '-',
        state: v ? `rs${v.readyState}${v.paused ? ' paused' : ''}` : '-',
        canvas: canvas ? `${canvas.width}x${canvas.height}` : 'none',
        frames: shot ? Math.round(shot.max) : (frameStatRef.current ? Math.round(frameStatRef.current.max) : null),
        decoded,
      });
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => clearInterval(t);
  }, [debugCam, scanning, nativeRes, regionId, videoEl]);

  /**
   * Watchdog for the iOS black preview (on the html5-qrcode engine).
   *
   * The failure signature is a video reporting 0x0 while claiming to play.
   * Flipping to the front camera and back fixes it by rebuilding the element,
   * so do that for the operator. Sustained blank frames count too, on a longer
   * fuse and a capped budget — a dark doorway must not become a restart loop.
   */
  useEffect(() => {
    if (!scanning || nativeRes) { blankRunRef.current = 0; return undefined; }
    const t = setInterval(async () => {
      if (restartingRef.current) return;

      const v = videoEl();
      const dead = !v || v.videoWidth === 0 || v.videoHeight === 0;
      const shot = v && !dead ? sampleFrame(v) : null;
      const blank = dead || (shot ? shot.max <= 8 : false);
      blankRunRef.current = blank ? blankRunRef.current + 1 : 0;

      const trigger = dead ? blankRunRef.current >= 2 : blankRunRef.current >= 5;
      if (!trigger || recoveriesRef.current >= 3) return;

      restartingRef.current = true;
      recoveriesRef.current += 1;
      blankRunRef.current = 0;
      try {
        const source = activeCamRef.current || { facingMode: { exact: 'environment' } };
        await teardown();
        const err = await attemptStart(source);
        if (err) throw err;
      } catch {
        setScanning(false);
        setCamError(blocked('CAMERA STOPPED', 'The camera stopped delivering frames and could not be restarted. Use PHOTO or a code.'));
      } finally {
        restartingRef.current = false;
      }
    }, 1200);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning, attemptStart, nativeRes]);

  // Leaving the page (or the DOOR tab) releases the camera.
  useEffect(() => () => {
    genRef.current += 1;
    if (liveScannerRef.current) { try { liveScannerRef.current.stop(); } catch { /* noop */ } }
    if (!qrRef.current) return;
    try { Promise.resolve(qrRef.current.stop()).catch(() => {}); } catch { /* already torn down */ }
  }, []);

  return {
    kind: k,
    // The offline queue, for the UI that wants to say "3 waiting to sync".
    queued: queue.queued,
    offline: queue.offline,
    flushQueue: queue.flush,
    regionId,
    nativeRes,
    // The button reads STOP the moment it's tapped; `live` is the first real frame.
    active: starting || scanning,
    live: scanning,
    camError,
    cameraCount: cameras.length,
    facingUser,
    flash,
    log,
    readingPhoto,
    debugCam,
    camStats,
    start,
    stop,
    toggle: () => (starting || scanning ? stop() : start()),
    flip,
    grabFrame,
    pickPhoto,
    handlePhoto,
    submitManual,
    checkIn,
    photoInputRef,
    liveVideoRef,
  };
}
